import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Skeleton Component
 *
 * Loading placeholder with shimmer animation.
 * Use to show content shape while loading.
 */

export function Skeleton({
  variant = 'rectangular',
  width,
  height,
  className,
  animated = true,
  ...props
}) {
  const variants = {
    rectangular: 'rounded-[var(--radius-md)]',
    circular: 'rounded-full',
    text: 'rounded-[var(--radius-sm)] h-4',
  };

  return (
    <div
      className={cn(
        'bg-[var(--color-neutral-150)]',
        variants[variant],
        animated && 'animate-shimmer',
        className
      )}
      style={{
        width: width,
        height: height,
      }}
      {...props}
    />
  );
}

/**
 * Text Skeleton - Multiple lines of text
 */
export function SkeletonText({
  lines = 3,
  lastLineWidth = '60%',
  gap = '0.5rem',
  className,
  ...props
}) {
  return (
    <div className={cn('space-y-2', className)} style={{ gap }} {...props}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          width={index === lines - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </div>
  );
}

/**
 * Avatar Skeleton
 */
export function SkeletonAvatar({ size = 'md', className, ...props }) {
  const sizes = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-20 h-20',
  };

  return (
    <Skeleton
      variant="circular"
      className={cn(sizes[size], className)}
      {...props}
    />
  );
}

/**
 * Contact Card Skeleton - Matches ContactCard layout
 */
export function ContactCardSkeleton({ className, ...props }) {
  return (
    <div
      className={cn(
        'bg-white',
        'rounded-[var(--radius-xl)]',
        'border border-[var(--color-border-subtle)]',
        'shadow-[var(--shadow-sm)]',
        'overflow-hidden',
        className
      )}
      {...props}
    >
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <SkeletonAvatar size="xl" />

          {/* Name and outlet */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Skeleton width="60%" height="1.25rem" />
              <Skeleton width="4rem" height="1rem" variant="text" />
            </div>
            <Skeleton width="80%" height="0.875rem" variant="text" className="mb-1.5" />
            <Skeleton width="40%" height="0.75rem" variant="text" />
          </div>
        </div>

        {/* Bio */}
        <div className="mt-4">
          <SkeletonText lines={2} lastLineWidth="75%" />
        </div>
      </div>

      {/* Tags section */}
      <div className="px-5 py-4 border-t border-[var(--color-border-subtle)] bg-[var(--color-neutral-25)]">
        <Skeleton width="5rem" height="0.75rem" variant="text" className="mb-3" />
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              width={`${Math.random() * 40 + 60}px`}
              height="1.5rem"
              className="rounded-[var(--radius-sm)]"
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-[var(--color-border-subtle)] flex items-center justify-between">
        <div className="flex gap-2">
          <Skeleton width="5rem" height="2rem" className="rounded-[var(--radius-md)]" />
          <Skeleton width="5rem" height="2rem" className="rounded-[var(--radius-md)]" />
        </div>
        <Skeleton width="6rem" height="2rem" className="rounded-[var(--radius-md)]" />
      </div>
    </div>
  );
}

/**
 * Filter Sidebar Skeleton
 */
export function FilterSidebarSkeleton({ className, ...props }) {
  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Search */}
      <Skeleton height="2.5rem" className="rounded-[var(--radius-lg)]" />

      {/* Filter sections */}
      {[1, 2, 3].map((section) => (
        <div key={section} className="space-y-3">
          <Skeleton width="6rem" height="0.875rem" variant="text" />
          <div className="space-y-2">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <Skeleton width="1rem" height="1rem" className="rounded" />
                <Skeleton width={`${Math.random() * 30 + 50}%`} height="0.875rem" variant="text" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Table Row Skeleton
 */
export function TableRowSkeleton({ columns = 4, className, ...props }) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 py-3 px-4',
        'border-b border-[var(--color-border-subtle)]',
        className
      )}
      {...props}
    >
      {Array.from({ length: columns }).map((_, index) => (
        <div key={index} className="flex-1">
          <Skeleton
            width={index === 0 ? '80%' : `${Math.random() * 40 + 40}%`}
            height="0.875rem"
            variant="text"
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Dashboard Stats Skeleton
 */
export function StatCardSkeleton({ className, ...props }) {
  return (
    <div
      className={cn(
        'bg-white rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] p-5',
        className
      )}
      {...props}
    >
      <Skeleton width="4rem" height="0.75rem" variant="text" className="mb-2" />
      <Skeleton width="6rem" height="2rem" className="mb-1" />
      <Skeleton width="5rem" height="0.625rem" variant="text" />
    </div>
  );
}

export default Skeleton;
